import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Brain, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_email", res.data.email);
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="marketing-bg min-h-screen flex items-center justify-center px-6 grid-bg">
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[#00F0FF]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-[#7000FF]/5 rounded-full blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-[#00F0FF] flex items-center justify-center mx-auto mb-4">
            <Brain size={32} className="text-black" />
          </div>
          <h1 className="font-outfit font-black text-3xl text-white">Admin Portal</h1>
          <p className="text-[#A1A1AA] text-sm mt-2 font-dm-sans">ShansoAI Hub Management System</p>
        </div>

        <div className="bg-zinc-900/80 border border-zinc-800 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2 font-dm-sans">Email Address</label>
              <input
                data-testid="admin-email-input"
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="admin@example.com"
                className="w-full bg-zinc-800/70 border border-zinc-700 focus:border-[#00F0FF] text-white px-4 py-3.5 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2 font-dm-sans">Password</label>
              <div className="relative">
                <input
                  data-testid="admin-password-input"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Enter your password"
                  className="w-full bg-zinc-800/70 border border-zinc-700 focus:border-[#00F0FF] text-white px-4 pr-12 py-3.5 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#00F0FF] transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 font-dm-sans">
                <AlertCircle size={15} className="flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00F0FF] text-black font-outfit font-black text-base py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <><Lock size={16} /> Sign In</>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-zinc-800">
          </div>
        </div>

        <button
          onClick={() => navigate("/")}
          className="mt-6 w-full text-[#A1A1AA] hover:text-white text-sm font-dm-sans text-center transition-colors"
        >
          Back to Website
        </button>
      </motion.div>
    </div>
  );
}
